import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, ChevronLeft, ChevronRight, Clock, Maximize2 } from "lucide-react";

const BLOG_POSTS = [
  {
    id: "wardrobe-essentials",
    title: "Wardrobe Essentials",
    subtitle: "Building a Timeless and Versatile Collection",
    author: "Saanvi Gupte",
    date: "2025",
    readTime: "4 min read",
    cover: "/blog/wardrobe-essentials/page-01.jpg",
    excerpt: "A complete style guide on building a timeless and versatile wardrobe — from the classic white shirt to the perfect knit. Click to read in full.",
    pages: 8,
    folder: "/blog/wardrobe-essentials",
  },
];

export default function Blog() {
  const [open, setOpen] = useState(null); // post object
  const [page, setPage] = useState(1);

  const openPost = (post) => { setPage(1); setOpen(post); };
  const closePost = () => setOpen(null);
  const next = () => setPage((p) => (open && p < open.pages ? p + 1 : 1));
  const prev = () => setPage((p) => (open && p > 1 ? p - 1 : open?.pages || 1));

  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="relative bg-[#F2EBE5] text-[#0B0E14] py-24 md:py-36 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-20">
          <div>
            <div className="flex items-center gap-3 text-[10px] tracking-overline uppercase text-[#8A5A53] mb-5">
              <span className="h-px w-10 bg-[#D18A58]" />
              Journal · 05
            </div>
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight">
              The <span className="italic text-[#D18A58]">journal</span>.
            </h2>
          </div>
          <p className="max-w-md text-sm md:text-base text-[#5C5E68] leading-relaxed">
            Notes on style, process and the philosophy behind a well-considered wardrobe.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          {BLOG_POSTS.map((post, i) => (
            <motion.article
              key={post.id}
              data-testid={`blog-card-${post.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.7, 0, 0.2, 1] }}
              className="group cursor-pointer"
              onClick={() => openPost(post)}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-[#0B0E14]/5 mb-5">
                <img
                  src={post.cover}
                  alt={post.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <div className="rounded-full bg-[#F2EBE5]/90 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-overline text-[#0B0E14]">
                    Style Guide · {post.pages} pages
                  </div>
                  <div className="h-10 w-10 rounded-full bg-[#F2EBE5]/95 text-[#0B0E14] flex items-center justify-center group-hover:bg-[#D18A58] group-hover:text-[#F2EBE5] transition-all">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-[10px] uppercase tracking-overline text-[#8A5A53] mb-3">
                <span>{post.date}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" />{post.readTime}</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl leading-tight mb-3 group-hover:text-[#D18A58] transition-colors">
                {post.title}
              </h3>
              <p className="text-sm md:text-base text-[#5C5E68] leading-relaxed">
                {post.excerpt}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-overline text-[#0B0E14] border-b border-[#0B0E14]/30 pb-1 group-hover:border-[#D18A58] group-hover:text-[#D18A58] transition-colors">
                Read the journal
                <Maximize2 className="h-3.5 w-3.5" />
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Reader Lightbox — displays PDF pages as-is */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="blog-detail-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePost}
            onKeyDown={(e) => {
              if (e.key === "Escape") closePost();
              if (e.key === "ArrowRight") next();
              if (e.key === "ArrowLeft") prev();
            }}
            tabIndex={-1}
            className="fixed inset-0 z-[80] bg-[#0B0E14]/95 backdrop-blur-2xl flex items-center justify-center p-4 md:p-10"
          >
            <button
              data-testid="blog-close-btn"
              onClick={(e) => { e.stopPropagation(); closePost(); }}
              className="absolute top-5 right-5 z-10 h-11 w-11 rounded-full bg-[#D18A58] text-[#0B0E14] flex items-center justify-center hover:bg-[#F2EBE5]"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              data-testid="blog-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-[#F2EBE5]/10 border border-white/15 backdrop-blur text-[#F2EBE5] flex items-center justify-center hover:bg-[#D18A58] hover:text-[#0B0E14]"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              data-testid="blog-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-[#F2EBE5]/10 border border-white/15 backdrop-blur text-[#F2EBE5] flex items-center justify-center hover:bg-[#D18A58] hover:text-[#0B0E14]"
              aria-label="Next page"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <motion.img
              key={page}
              src={`${open.folder}/page-${String(page).padStart(2, "0")}.jpg`}
              alt={`${open.title} — page ${page}`}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[95vw] max-h-[90vh] w-auto h-auto object-contain rounded-sm shadow-2xl"
            />

            <div className="absolute bottom-5 left-0 right-0 text-center text-[10px] uppercase tracking-overline text-[#F2EBE5]/70">
              {open.title} · Page {String(page).padStart(2, "0")} of {open.pages} · Esc to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
